import React, { useState, useContext } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Divider,
  Dialog,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import PersonIcon from '@mui/icons-material/Person'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EventIcon from '@mui/icons-material/Event'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import PaymentForm from 'pages/MyAccount/PaymentForm'
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog'
import { PaymentContext } from 'contexts/PaymentContext'
import { NewPaymentInfo, PaymentInfo, PaymentMethod } from 'hooks/usePayment'
import { LoadingContext } from 'contexts/LoadingContext'
import { getCardTypeIcon } from 'utils/cards'
import { useTranslation } from 'react-i18next'

const PaymentCard = () => {
  const { t } = useTranslation()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<
    string | null
  >(null)
  const [isPaymentFormOpen, setIsPaymentFormOpen] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null)

  const {
    paymentMethods,
    savePaymentInfo,
    deletePaymentMethod,
    updatePaymentMethod,
    isLoadingPaymentMethods
  } = useContext(PaymentContext)
  const [mode, setMode] = useState<'add' | 'edit'>('add')

  const handleEditPaymentMethod = (method: PaymentMethod) => {
    setSelectedPaymentMethod(method)
    setMode('edit')
    setIsPaymentFormOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (paymentMethodToDelete !== null) {
      showLoading()
      await deletePaymentMethod(paymentMethodToDelete)
      setDeleteDialogOpen(false)
      setPaymentMethodToDelete(null)
      stopLoading()
    }
  }

  const handleAddPaymentMethod = () => {
    setSelectedPaymentMethod(null)
    setMode('add')
    setIsPaymentFormOpen(true)
  }

  const handleFormClose = () => {
    setIsPaymentFormOpen(false)
  }
  const { showLoading, stopLoading } = useContext(LoadingContext)

  const handleFormSubmit = async (
    paymentDetails: NewPaymentInfo | PaymentInfo
  ) => {
    showLoading()
    try {
      if ('paymentProfileId' in paymentDetails) {
        await updatePaymentMethod(paymentDetails as PaymentInfo)
      } else {
        await savePaymentInfo(paymentDetails as NewPaymentInfo)
      }
      setIsPaymentFormOpen(false)
    } catch (error) {
      console.error('Error submitting payment details:', error)
    }
    stopLoading()
  }

  return (
    <Box sx={{ mt: 2, mb: 6 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={handleAddPaymentMethod}
          sx={{
            borderRadius: '6px',
            px: 2,
            py: 1,
            fontWeight: 400,
            textTransform: 'none'
          }}
        >
          {t('myAccount.paymentMethods.addCard')}
        </Button>
      </Box>

      {isLoadingPaymentMethods ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px'
          }}
        >
          <CircularProgress />
        </Box>
      ) : paymentMethods && paymentMethods.length > 0 ? (
        <Grid container spacing={3}>
          {paymentMethods.map(method => (
            <Grid item xs={12} md={6} key={method.paymentProfileId}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2
                  }}
                >
                  <Box
                    component='img'
                    src={getCardTypeIcon(method.cardType ?? 'default')}
                    alt={method.cardType}
                    sx={{
                      height: '40px',
                      objectFit: 'contain'
                    }}
                  />
                  <Chip
                    icon={<EventIcon sx={{ fontSize: 14 }} />}
                    label={`${t('myAccount.paymentMethods.exp')} ${
                      method.expirationDate
                    }`}
                    size='small'
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      bgcolor: 'grey.100'
                    }}
                  />
                </Box>

                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <CreditCardIcon
                    sx={{ color: 'primary.main', fontSize: 20 }}
                  />
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 1
                    }}
                  >
                    {method.cardNumber}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5
                  }}
                >
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                  <Typography variant='body2' color='text.secondary'>
                    {method.billingAddress?.firstName}{' '}
                    {method.billingAddress?.lastName}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <LocationOnIcon
                    sx={{ color: 'text.secondary', fontSize: 18, mt: 0.3 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ lineHeight: 1.6 }}
                    >
                      {method.billingAddress?.address}
                      <br />
                      {method.billingAddress?.city},{' '}
                      {method.billingAddress?.state}{' '}
                      {method.billingAddress?.zipCode}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                >
                  <Button
                    variant='outlined'
                    size='small'
                    startIcon={<EditIcon />}
                    onClick={() => handleEditPaymentMethod(method)}
                    sx={{
                      borderRadius: '6px',
                      px: 2,
                      textTransform: 'none',
                      fontWeight: 400
                    }}
                  >
                    {t('myAccount.paymentMethods.edit')}
                  </Button>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setPaymentMethodToDelete(method.paymentProfileId)
                      setDeleteDialogOpen(true)
                    }}
                    sx={{
                      color: 'primary.main'
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3
          }}
        >
          <CreditCardIcon
            sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}
          />
          <Typography variant='h6' color='text.secondary' gutterBottom>
            {t('myAccount.paymentMethods.noPaymentMethods')}
          </Typography>
        </Paper>
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDelete.deletePaymentMethod')}
        message={t('confirmDelete.confirmMessage')}
      />

      <Dialog
        open={isPaymentFormOpen}
        onClose={handleFormClose}
        fullWidth
        maxWidth='sm'
      >
        <PaymentForm
          initialValues={{
            paymentProfileId: selectedPaymentMethod?.paymentProfileId || '',
            cardNumber: selectedPaymentMethod?.cardNumber || '',
            expirationDate: selectedPaymentMethod?.expirationDate || '',
            cvv: '',
            billingAddress: {
              firstName: selectedPaymentMethod?.billingAddress?.firstName || '',
              lastName: selectedPaymentMethod?.billingAddress?.lastName || '',
              addressLine1:
                selectedPaymentMethod?.billingAddress?.address.split(', ')[0] ||
                '',
              addressLine2:
                selectedPaymentMethod?.billingAddress?.address.split(', ')[1] ||
                '',
              city: selectedPaymentMethod?.billingAddress?.city || '',
              state: selectedPaymentMethod?.billingAddress?.state || '',
              zipCode: selectedPaymentMethod?.billingAddress?.zipCode || ''
            }
          }}
          onSubmit={handleFormSubmit}
          onCancel={handleFormClose}
          mode={mode}
        />
      </Dialog>
    </Box>
  )
}

export default PaymentCard
